import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Trophy, TrendingUp } from "lucide-react";
import PageLayout from "../../components/layout/PageLayout";
import TrendingBar from "../../components/football/TrendingBar";
import { HERO_IMAGES } from "../../lib/constants";
import api from "../../lib/api";

export default function Fantasy() {
    const [owners, setOwners] = useState([]);
    const [cfg, setCfg] = useState(null);
    const [tab, setTab] = useState("standings");

    useEffect(() => {
        api.get("/football/fantasy/owners").then(r => setOwners(r.data)).catch(() => {});
        api.get("/football/config").then(r => setCfg(r.data)).catch(() => {});
    }, []);

    const sortedStandings = [...owners].sort((a,b) => a.league_rank - b.league_rank);
    const sortedPower    = [...owners].sort((a,b) => a.power_ranking - b.power_ranking);

    return (
        <PageLayout>
            <section className="relative pt-28 pb-14 border-b border-white/10 overflow-hidden">
                <img src={HERO_IMAGES.football} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-5">Digital Beast Fantasy Football</div>
                    <h1 className="font-display text-5xl md:text-7xl text-white leading-[0.9] tracking-tight" data-testid="fantasy-heading">
                        10 OWNERS.<br/>ONE LEAGUE.<br/><span className="text-[#d4af37]">ONE CHAMPION.</span>
                    </h1>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <a href={cfg?.espn_league_url || "#"} target="_blank" rel="noreferrer" className="btn-gold" data-testid="enter-espn-btn">
                            Enter the Digital Beast ESPN League <ExternalLink size={14} />
                        </a>
                        <Link to="/football" className="btn-outline" data-testid="fantasy-back-hub">Back to Football</Link>
                    </div>
                </div>
            </section>

            <TrendingBar />

            {/* OWNER GRID */}
            <section className="py-14" data-testid="fantasy-owners-section">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-5">The Ten Owners</div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
                        {owners.map(o => (
                            <Link key={o.id} to={`/football/fantasy/owners/${o.slug}`}
                                className="surface surface-hover overflow-hidden group" data-testid={`owner-card-${o.slug}`}>
                                <div className="relative aspect-[4/5]">
                                    <img src={o.photo} alt={o.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                                    <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
                                        <div className="bg-[#d4af37] text-black font-display text-lg leading-none w-9 h-9 flex items-center justify-center">{o.league_rank}</div>
                                        <div className="text-right">
                                            <div className="text-[#d4af37] font-mono text-xs">{o.record_wins}-{o.record_losses}</div>
                                            <div className="text-white/50 text-[9px] uppercase tracking-widest">{o.streak}</div>
                                        </div>
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 p-4">
                                        <div className="font-display text-xl text-white leading-tight">{o.team_name}</div>
                                        <div className="text-white/60 text-[10px] uppercase tracking-[0.24em] mt-1">{o.name}</div>
                                        <div className="text-white/40 text-[10px] mt-1">{o.city}</div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* LEADERBOARDS */}
            <section className="py-10 border-t border-white/10 bg-[#0a0a0a]" data-testid="fantasy-leaderboards">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="flex gap-2 mb-6">
                        {[["standings","ESPN Standings"],["power","DB Power Ranking"]].map(([k,l]) => (
                            <button key={k} onClick={() => setTab(k)} data-testid={`fantasy-tab-${k}`}
                                className={`px-5 py-3 border ${tab === k ? "border-[#d4af37] text-[#d4af37]" : "border-white/10 text-white/60"} text-xs uppercase tracking-widest font-semibold`}>{l}</button>
                        ))}
                    </div>
                    <div className="surface overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-white/40 text-xs uppercase tracking-widest">
                                    <th className="text-left p-4">Rank</th>
                                    <th className="text-left p-4">Team</th>
                                    <th className="text-left p-4">Owner</th>
                                    <th className="text-right p-4">W-L</th>
                                    <th className="text-right p-4">PF</th>
                                    <th className="text-right p-4">PA</th>
                                    <th className="text-left p-4">Streak</th>
                                    <th className="text-left p-4">Next</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(tab === "standings" ? sortedStandings : sortedPower).map(o => (
                                    <tr key={o.id} className="border-t border-white/10 hover:bg-white/5">
                                        <td className="p-4"><span className="font-display text-2xl text-[#d4af37]">{tab === "standings" ? o.league_rank : o.power_ranking}</span></td>
                                        <td className="p-4"><Link to={`/football/fantasy/owners/${o.slug}`} className="text-white hover:text-[#d4af37]">{o.team_name}</Link></td>
                                        <td className="p-4 text-white/70">{o.name}</td>
                                        <td className="p-4 text-right text-white font-mono">{o.record_wins}-{o.record_losses}</td>
                                        <td className="p-4 text-right text-white/70 font-mono">{o.points_for}</td>
                                        <td className="p-4 text-right text-white/40 font-mono">{o.points_against}</td>
                                        <td className="p-4 text-white/60">{o.streak}</td>
                                        <td className="p-4 text-white/50 text-xs">{o.weekly_opponent}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* WEEKLY CATEGORIES */}
            <section className="py-14" data-testid="fantasy-categories">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-5">Weekly Beast Reports</div>
                    <h2 className="font-display text-3xl md:text-4xl text-white tracking-tight mb-8">Editorial coverage every week</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            ["Beast of the Week", Trophy],["Bust of the Week", TrendingUp],
                            ["Waiver Wire", TrendingUp],["Injury Watch", TrendingUp],
                            ["Start Him", TrendingUp],["Sit Him", TrendingUp],
                            ["Matchup of the Week", Trophy],["Trade Watch", TrendingUp],
                        ].map(([l, I]) => (
                            <div key={l} className="surface p-5" data-testid={`fantasy-cat-${l.toLowerCase().replace(/\s+/g,'-')}`}>
                                <I className="text-[#d4af37]" size={20} strokeWidth={1.5} />
                                <div className="font-display text-lg text-white mt-4">{l}</div>
                                <div className="text-white/40 text-xs mt-2">Editor-published weekly</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </PageLayout>
    );
}
